import { ReactElement } from 'react';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { Fund } from '../../../../../types/server-data/ClientConfig';
import {
  extractProspectusRestrictionsPayload,
  type ProspectusRestrictionsPageModel,
  type ProspectusRestrictionsPayload,
  type ProspectusRestrictionsTimeSeries,
} from '../../../../../api/rs-hg/risk/prospectusRestrictions.types';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import SecondLevelTable from './second-level-table/second-level-table.component';
import RaptorStatusBox from '../../../../feedback/RaptorStatusBox.component';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import NotesComp from '../../../sharedComponents/notes/Notes';
import ProspectusRestrictionsTimeSeriesCell from './ProspectusRestrictionsTimeSeriesCell';

export interface IProspectusRestrictionsFirstLevelData {
  number: number;
  name: string;
  status: Status;
  value: number | string;
  limit: number | string;
  timeSeries: ProspectusRestrictionsTimeSeries | null;
}

export const csvProspectusRestrictionsFirstLevel = [
  'number',
  'name',
  'status',
  'value',
  'limit',
];

export const firstLevelColumns = (
  section: string,
  fundId: string,
  fundName: string,
  positionDate: string,
  shouldCallNotes = true,
): CustomColumn<IProspectusRestrictionsFirstLevelData>[] => [
    { title: '#', field: 'number', width: '0.62rem' },
    { title: 'Name', field: 'name' },
    {
      title: 'Value',
      field: 'value',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: { textAlign: 'center' },
    },
    {
      title: 'Limit',
      field: 'limit',
      cellStyle: { textAlign: 'center' },
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData: IProspectusRestrictionsFirstLevelData) => (
        <RaptorStatusBox status={specifyStatus(rowData.status)} />
      ),
      width: '8%',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Time Series',
      field: 'time_series',
      render: (rowData: IProspectusRestrictionsFirstLevelData) => (
        <ProspectusRestrictionsTimeSeriesCell
          fundName={fundId}
          ruleIndex={rowData.number}
          ruleName={rowData.name}
          initialTimeSeries={rowData.timeSeries}
        />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        padding: '0.62rem 0',
        textAlign: 'center',
        width: '50%',
      },
    },
    {
      title: 'Notes',
      field: 'notes',
      width: '3.12rem',
      render: (rowData: IProspectusRestrictionsFirstLevelData) => (
        <NotesComp
          positionDate={positionDate}
          fundId={fundId}
          fundName={`${fundName}`}
          subject={`${section}_prospectus-restrictions`}
          topic={`rule_${rowData.number}`}
          shouldCall={shouldCallNotes}
        />
      ),
      cellStyle: {
        padding: '1.00rem',
        textAlign: 'center',
        paddingRight: '1.50rem',
        paddingLeft: '0.50rem',
      },
      headerStyle: {
        textAlign: 'center',
        padding: 'auto 1.00rem',
        paddingRight: '1.50rem',
        paddingLeft: '0.50rem',
      },
    },
  ];

export function useBuildFirstLevelProspectusRestrictionsData(
  payload: ProspectusRestrictionsPayload | undefined,
): IProspectusRestrictionsFirstLevelData[] {
  if (!payload?.limit_status?.length) {
    return [];
  }

  const returnData: IProspectusRestrictionsFirstLevelData[] = [];
  payload.limit_status.forEach((row, index) => {
    if (index > 0) {
      const timeSeries = row[5];
      const hasTimeSeries =
        Array.isArray(timeSeries) && timeSeries.length > 2;

      returnData.push({
        number: index,
        name: row[0],
        value: row[3],
        limit: row[2],
        status: row[1] as Status,
        timeSeries: hasTimeSeries
          ? (timeSeries as ProspectusRestrictionsTimeSeries)
          : null,
      });
    }
  });

  return returnData;
}

interface IProspectusRestrictionsFirstLevelTableProps {
  activeFund: string;
  pageModel: ProspectusRestrictionsPageModel;
  isFetching?: boolean;
  activeFundName: string;
  fundDetails: Fund;
  section: string;
}

function FirstLevelTable({
  pageModel,
  activeFund,
  activeFundName,
  fundDetails,
  section,
  isFetching = false,
}: IProspectusRestrictionsFirstLevelTableProps): ReactElement {
  const payload = extractProspectusRestrictionsPayload(pageModel)!;
  const tableData = useBuildFirstLevelProspectusRestrictionsData(payload);
  const positionDate =
    pageModel.selected_position_date ||
    pageModel.returned_position_date ||
    '';

  return (
    <GridItem xs={12} card>
      <CustomTable<IProspectusRestrictionsFirstLevelData>
        selectedPositionDate={positionDate}
        columns={firstLevelColumns(
          section,
          activeFund,
          activeFundName,
          positionDate,
          !isFetching,
        )}
        csvFields={csvProspectusRestrictionsFirstLevel}
        showToolbar={true}
        data={tableData}
        options={{
          exportButton: true,
          search: true,
          paging: false,
          raptorExport: true,
        }}
        toolbarComponents={{
          toolbarTitle: fundDetails?.name,
        }}
        onRowClick={(event: any, rowData, togglePanel: any) => {
          return togglePanel!();
        }}
        detailPanel={[
          {
            render: (rowData) => {
              return (
                <SecondLevelTable
                  selectedPositionDate={positionDate}
                  rowData={rowData as IProspectusRestrictionsFirstLevelData}
                  report={payload}
                  fundDetails={fundDetails}
                  section={section}
                />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
}

export default FirstLevelTable;
